<template>
    <div class="page-layout">
        <div class="header-section">
            <search-filter-manager
                v-if="gridApi"
                :grid-api="gridApi"
                :grid-url="'/api/invoices/team-member/grid/saved'"
                :refresh-server-side="refreshServerSide"
                @filter-loaded="filterHasLoaded"
                @onSearch="handleFilterOrSearch"
            />
            <div class="generate-invoice-section" v-if="!isTeamMember">
                <md-button title="Generate Invoices" class="md-primary md-just-icon md-round" @click="createInvoice">
                    <md-icon>library_add</md-icon>
                </md-button>
            </div>
        </div>
        <div class="page-content">
            <bulk-actions
                :selected-invoices="selectedRows"
                :trigger-sync-to-accounting-package="triggerSyncToAccountingPackage"
                @refresh="refreshGrid"
            />
            <ag-grid-vue
                class="grid-display"
                :class="gridThemeClass"
                row-model-type="serverSide"
                :column-defs="columnDefs"
                :default-col-def="defaultColDef"
                :grid-options="gridOptions"
                :suppress-excel-export="false"
                :row-selection="rowSelection"
                :side-bar="gridSideBar"
                @grid-ready="onGridReady"
                @selection-changed="onSelectionChanged"
            />
        </div>
        <div class="progress-container" v-if="showProgressBox">
            <div class="button-close">
                <md-button class="md-default md-just-icon md-round" @click="handleCloseProgressBox">
                    <md-icon>close</md-icon>
                </md-button>
            </div>
            <div class="progress-text-section" v-if="!isProgressComplete">
                <div>
                    <div class="btn-loader route-loader"></div>
                </div>
                <div v-if="totalInvoice == 0">Preparing invoices...</div>
                <div v-else>{{ currentProcessedInvoice }} of {{ totalInvoice }} invoices have been generated.</div>
            </div>
            <div v-else class="progress-text-section">
                <div>
                    <md-icon class="green-check">check_circle</md-icon>
                </div>
                <div>
                    Process has been completed.
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridVue } from 'ag-grid-vue';
import LinkRenderer from '@/AgGridComponents/link-renderer';
import PageHeaderCheckbox from '@/AgGridComponents/page-header-checkbox';
import { GeneralMixin, AgGridMixin, InvoicesAgGridMixin } from '@/mixins';
import SearchFilterManager from '@/AgGridComponents/SearchFilterManager';
import { PROCESS_CHARGES_DATA_DEFAULT } from '@/pages/ProcessChargesAgGrid/processChargesConstants';
import { FeatureManager } from '@/directives';
import { HUB_NAMES, HUB_ACTIONS, SIGNALR_CONNECTION_STATE } from '@/utils/constants';
import { mapGetters, mapActions } from 'vuex';
import { handleRequests } from '@/helpers';
import ActionRendererTeamMemberInvoice from './ActionRendererTeamMemberInvoice';
import { buildTeamMemberInvoiceColumns } from './teamMemberInvoiceColumns';
import BulkActions from './BulkActions';
import CreateInvoicesModal from '../../TeamMemberInvoices/components/CreateInvoicesModal';

export default {
    name: 'TeamMemberInvoice',
    components: {
        AgGridVue,
        SearchFilterManager,
        // eslint-disable-next-line vue/no-unused-components
        LinkRenderer,
        // eslint-disable-next-line vue/no-unused-components
        PageHeaderCheckbox,
        // eslint-disable-next-line vue/no-unused-components
        ActionRendererTeamMemberInvoice,
        BulkActions
    },
    mixins: [GeneralMixin, AgGridMixin, FeatureManager, InvoicesAgGridMixin],
    data() {
        return {
            ...PROCESS_CHARGES_DATA_DEFAULT,
            columnDefs: [],
            searchText: '',
            hasGridFilterLoaded: false,
            invoiceKeysToSum: [],
            selectedRows: [],
            signalRHandlers: [],
            signalRTimeout: null,
            totalKeys: [
                'subTotal',
                'totalTax',
                'total',
                'proRataAmount',
                'weeklyMinimum',
                'totalAdjustments',
                'totalMinimumPay'
            ],
            tags: [],
            totalInvoiceToBeGenerated: 0,
            invoiceGenerated: 0,
            showProgressBox: false,
            isProgressComplete: false,
            currentProcessedInvoice: 0,
            totalInvoice: 0
        };
    },
    created() {
        this.gridOptions.getRowId = (params) => params.data.teamMemberInvoiceId;

        // Prevent selection of OnHold invoices
        this.gridOptions.isRowSelectable = (rowNode) => {
            return rowNode.data?.status !== 'OnHold';
        };

        // pagination change hook for pinned totals
        this.gridOptions.onPaginationChanged = (params) => {
            this.updateCacheBlockDefaultSize(params.api);
            this.$_updatePageTotals(params.api, this.totalKeys);
        };
        this.buildColumns();
        this.getTags();
    },
    mounted() {
        this.signalRHandlers = [
            {
                action: 'TeamMemberInvoiceBatchCompleted',
                handler: async (batchId, status, completedInvoiceCount) => {
                    this.handleTeamMemberInvoiceBatchCompleted(batchId, status, completedInvoiceCount);
                }
            },
            {
                action: 'TeamMemberInvoiceBatchFailed',
                handler: async (status, completedInvoiceCount, failedInvoiceCount) => {
                    this.handleTeamMemberInvoiceBatchFailed(status, completedInvoiceCount, failedInvoiceCount);
                }
            },
            {
                action: 'InvoiceSentResult',
                handler: this.handleInvoiceSentResult
            },
            {
                action: 'BulkInvoiceResult',
                handler: this.handleBulkInvoiceResult
            },
            {
                action: 'TotalInvoiceToBeGenerated',
                handler: this.handleTotalInvoiceToBeGenerated
            },
            {
                action: 'InvoiceGenerated',
                handler: this.handleInvoiceGenerated
            },
            {
                action: 'TeamMemberInvoiceDraftSent',
                handler: this.updateDraftInvoiceHasBeenSent
            }
        ];

        this.setupSignalR();
    },
    beforeDestroy() {
        clearTimeout(this.signalRTimeout);
        this.removeSignalRHandlers();
    },
    computed: {
        ...mapGetters({
            getTeamMembers: 'team/teamMembers',
            getTeamRegions: 'team/teamRegions',
            getConnectionStatus: 'signalR/getConnectionStatus',
            getConnection: 'signalR/getConnection',
            isTeamMember: 'user/isTeamMember'
        })
    },
    methods: {
        ...mapActions('team', ['FETCH_TEAM_MEMBERS', 'FETCH_TEAM_REGIONS']),
        onGridReady(params) {
            this.gridApi = params.api;

            if (!this.hasGridFilterLoaded) 
                return;

            this.setupDataList();
        },
        buildColumns() {
            this.columnDefs = buildTeamMemberInvoiceColumns(this);
        },
        onSelectionChanged() {
            const selectedRows = this.gridApi.getSelectedRows();
            this.selectedRows = selectedRows;
        },
        refreshGrid() {
            if (this.gridApi) {
                this.selectedRows = [];
                this.gridApi.deselectAll();
                this.gridApi.refreshServerSide({ purge: true });
            }
        },
        setupDataList() {
            const datasource = this.$_createDataSource(
                '/api/invoices/team-member',
                this.gridApi,
                () => this.searchText,
                null, // no data transformer
                null, // no grand totals callback
                true // requireFilters - only load data when filters are applied
            );
            this.gridApi.setServerSideDatasource(datasource);
        },
        filterHasLoaded() {
            this.hasGridFilterLoaded = true;
            this.setupDataList();
        },
        // eslint-disable-next-line func-names
        handleFilterOrSearch(val) {
            this.searchText = val;
            this.gridApi.refreshServerSide({ purge: true });
        },
        refreshServerSide() {
            this.gridApi.showLoadingOverlay();
            this.gridApi.refreshServerSide({ purge: true });
        },
        updateCacheBlockDefaultSize(api) {
            const currentPageSize = api.paginationGetPageSize();
            api.setCacheBlockSize(currentPageSize);
        },
        async loadTeamMembers(params) {
            let teamMemberList = [];

            if (this.getTeamMembers.length <= 0) {
                teamMemberList = await this.FETCH_TEAM_MEMBERS();
            } else {
                teamMemberList = this.getTeamMembers;
            }

            params.success(teamMemberList);
        },
        async loadTeamRegions(params) {
            let teamRegionList = [];

            if (this.getTeamRegions.length <= 0) {
                teamRegionList = await this.FETCH_TEAM_REGIONS();
            } else {
                teamRegionList = this.getTeamRegions;
            }

            teamRegionList.push({ name: '(Blank)', teamRegionId: null });
            params.success(teamRegionList);
        },
        async loadCompanies(params) {
            let companyList = [];

            const { data } = await handleRequests('/api/teams/companies');
            companyList = Array.isArray(data)
                ? data
                    .filter((c) => c && (c.name || c.companyName || c.company))
                    .map((c) => ({ name: c.name || c.companyName || c.company }))
                : [];

            companyList.push({ name: '(Blank)' });
            params.success(companyList);
        },
        async loadBatchFilters(params) {
            let batchList = [];

            const { data } = await handleRequests('/api/team-member-invoices/batch-list');
            batchList = data.map((c) => ({
                name: `${this.$_formatDate(c.startDate)} - ${this.$_formatDate(c.endDate)}`,
                teamMemberInvoiceBatchId: c.invoiceBatchId
            }));

            batchList.push({ name: '(Blank)', teamMemberInvoiceBatchId: null });
            params.success(batchList);
        },
        async setupSignalR() {
            try {
                await this.$store.dispatch('signalR/connect', HUB_NAMES.InvoicingHub);
                const connection = this.getConnection(HUB_NAMES.InvoicingHub);

                // Verify connection is established before proceeding
                if (!connection || connection.state !== SIGNALR_CONNECTION_STATE.Connected) {
                    throw new Error('SignalR connection not established');
                }

                await connection.invoke(HUB_ACTIONS.JoinToTeamChannel);
                await this.$store.dispatch('signalR/registerHandlers', {
                    hubName: HUB_NAMES.InvoicingHub,
                    handlers: this.signalRHandlers
                });
            } catch (e) {
                console.error('Error connecting to invoicing hub:', e);
            }
        },
        removeSignalRHandlers() {
            try {
                const connection = this.getConnection(HUB_NAMES.InvoicingHub);

                if (connection && connection.state === SIGNALR_CONNECTION_STATE.Connected) {
                    this.$store.dispatch('signalR/unregisterHandlers', {
                        hubName: HUB_NAMES.InvoicingHub,
                        handlers: this.signalRHandlers
                    });
                    connection.invoke(HUB_ACTIONS.RemoveFromTeamChannel);
                }
            } catch (error) {
                console.error('Error removing SignalR handlers:', error);
            }
        },
        handleTeamMemberInvoiceBatchCompleted(batchId, status, completedInvoiceCount) {
            if (status === 'CompletedNoRecords') {
                this.$notifySuccess('Invoice batch completed but no uninvoiced stops found in range.', 60000);
            } else {
                let successMessage = `${completedInvoiceCount} Invoices have been generated.`;
                if (completedInvoiceCount === 1) {
                    successMessage = `${completedInvoiceCount} Invoice has been generated.`;
                }
                this.$notifySuccess(successMessage, 60000);
            }
        },
        handleTeamMemberInvoiceBatchFailed(status, completedInvoiceCount, failedInvoiceCount) {
            this.$notify({
                message: `Completed Invoices: ${completedInvoiceCount} Failed Invoices: ${failedInvoiceCount}.<br /><br /> Please try again. If error persists please contact support.`,
                type: 'warning',
                duration: 10000
            });
        },
        handleInvoiceSentResult(invoiceId, reference, note) {
            const rowNode = this.gridApi.getRowNode(invoiceId.toString());

            if (rowNode === null) {
                return;
            }

            const rowDataToUpdate = {
                teamMemberInvoiceId: invoiceId.toString(),
                ...rowNode.data
            };

            rowDataToUpdate.accountingSystemRef = reference;
            rowDataToUpdate.accountingSystemNote = note;
            rowDataToUpdate.isSyncing = false;

            this.gridApi.applyServerSideTransaction({ update: [rowDataToUpdate] });
        },
        async handleBulkInvoiceResult(result, invoices) {
            this.$_handleLoaderState(false);

            let successMessage = 'Successfully processed the invoices.';
            let errorMessage = 'Error in sending the invoices to the accounting package.';

            if (invoices?.length) {
                if (invoices.length === 1) {
                    successMessage = 'Successfully processed the invoice.';
                    errorMessage = 'Error in sending the invoice to the accounting package.';
                }
                const rowDataToUpdates = [];

                invoices.forEach((invoice) => {
                    const rowNode = this.gridApi.getRowNode(invoice.InvoiceId.toString());

                    const rowDataToUpdate = {
                        teamMemberInvoiceId: invoice.InvoiceId.toString(),
                        ...rowNode.data
                    };

                    rowDataToUpdate.accountingSystemRef = invoice.Reference;
                    rowDataToUpdate.accountingSystemNote = invoice.Note;
                    rowDataToUpdate.isSyncing = false;

                    rowDataToUpdates.push(rowDataToUpdate);
                });

                this.gridApi.applyServerSideTransaction({ update: rowDataToUpdates });
            }

            if (result === 'Success') {
                this.$notifySuccess(successMessage, 10000);
            } else {
                this.$notifyError(errorMessage);
            }
        },
        updateDraftInvoiceHasBeenSent(invoiceId) {
            const rowNode = this.gridApi.getRowNode(invoiceId.toString());

            if (rowNode === null) {
                return;
            }

            const rowDataToUpdate = {
                teamMemberInvoiceId: invoiceId.toString(),
                ...rowNode.data
            };

            rowDataToUpdate.draftEmailHasBeenSent = true;
            rowDataToUpdate.lastDraftEmailDateSent = moment().format(DATE_TYPES.standardDateTime);
            rowDataToUpdate.newDraftCostAdded = false;

            this.gridApi.applyServerSideTransaction({ update: [rowDataToUpdate] });
        },
        async fetchInvoice(invoiceId) {
            try {
                const api = `/api/invoices/team-member/grid/search`;
                const payload = {
                    method: 'post',
                    data: {
                        startRow: 0,
                        endRow: 1,
                        filterModel: {
                            teamMemberInvoiceId: { filterType: 'number', type: 'equals', filter: invoiceId }
                        },
                        sortModel: [],
                        rowGroupCols: [],
                        valueCols: [],
                        groupKeys: [],
                        pivotCols: []
                    }
                };

                const response = await handleRequests(api, payload);
                const rows = response?.data?.responses || [];

                if (rows.length > 0) {
                    const rowDataToUpdate = {
                        teamMemberInvoiceId: invoiceId.toString(),
                        ...rows[0]
                    };

                    this.gridApi.applyServerSideTransaction({ update: [rowDataToUpdate] });
                }
            } catch (e) {
                console.error('Error fetching invoice:', e);
            }
        },
        triggerSyncToAccountingPackage(invoiceIds) {
            if (!this.gridApi || !invoiceIds || invoiceIds.length === 0) {
                return;
            }

            // Set isSyncing to true for all selected invoices
            invoiceIds.forEach((invoiceId) => {
                const rowNode = this.gridApi.getRowNode(invoiceId.toString());

                if (rowNode && rowNode.data) {
                    if (rowNode.data.isSyncing === undefined) {
                        this.$set(rowNode.data, 'isSyncing', true);
                    } else {
                        rowNode.data.isSyncing = true;
                    }
                    this.gridApi.refreshCells({ rowNodes: [rowNode] });
                }
            });
        },
        async createInvoice() {
            this.$modal.show(CreateInvoicesModal, { tags: this.tags }).then((response) => {
                if (response) {
                    this.isProgressComplete = false;
                    this.showProgressBox = true;
                    this.currentProcessedInvoice = 0;
                    this.totalInvoice = 0;
                }
            });
        },
        async getTags() {
            const api = `/api/teams/settings/tags?showDeletedTags=false`;
            try {
                const response = await handleRequests(api);
                if (response?.data) 
                    this.tags = [...response.data];
            } catch (error) {
                const message = 'Error in getting tags';
                this.$_showError(message);
            }
        },
        handleTotalInvoiceToBeGenerated(totalCount) {
            this.totalInvoice = Number(totalCount);
            this.currentProcessedInvoice = 0;
        },
        handleInvoiceGenerated(invoiceId) {
            // Update progress box
            this.currentProcessedInvoice = this.currentProcessedInvoice + 1;

            if (this.currentProcessedInvoice >= this.totalInvoice) {
                this.isProgressComplete = true;

                // Auto-close after 3 seconds on completion
                setTimeout(() => {
                    this.handleCloseProgressBox();
                    // Refresh the grid to show new invoices
                    this.refreshGrid();
                }, 3000);
            }
        },
        handleCloseProgressBox() {
            this.showProgressBox = false;
        }
    }
};
</script>
<style scoped lang="scss">
.page-layout {
    display: flex;
    flex-direction: column;
    height: 100%;
}
.page-content {
    flex: 1 1 auto;
    min-height: 0;
}
.header-section {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
}
.generate-invoice-section {
    display: flex;
    flex-shrink: 0;
    align-items: flex-start;

    ::v-deep .md-button {
        width: 30px;
        height: 30px;
        min-width: 30px;
        min-height: 30px;

        .md-icon {
            font-size: 18px;
        }
    }
}
.grid-display {
    width: 100%;
    height: calc(100vh - 160px);
}
.actions-cell {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
}
.actions-cell .md-button {
    min-width: 60px;
    padding: 2px 6px;
    font-size: 11px;
}

.green-check {
    color: #4caf50 !important;
}

.progress-container {
    min-width: 200px;
    max-width: 250px;
    background-color: #fff;
    position: fixed;
    right: 12px;
    bottom: 8px;
    z-index: 99;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.14);
    padding: 0 10px 10px 10px;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;

    .progress-text-section {
        display: table;
        padding-top: 12px;
        > div {
            display: table-cell;
            vertical-align: middle;
        }

        > div:last-child {
            padding-left: 10px;
        }
    }

    ::v-deep .md-button {
        height: 20px;
        min-width: 20px;
        width: 20px;
        position: absolute;
        top: -12px;
        right: -12px;

        i {
            font-size: 15px !important;
        }
    }
}

.btn-loader {
    border: 4px solid #eeeeee;
    border-radius: 50%;
    border-top: 4px solid #2b93ff;
    width: 24px;
    height: 24px;
    -webkit-animation: spin 1s linear infinite;
    animation: spin 1s linear infinite;
}

.route-loader {
    top: 20% !important;
    left: 0 !important;
}

@-webkit-keyframes spin {
    0% {
        -webkit-transform: rotate(0deg);
    }
    100% {
        -webkit-transform: rotate(360deg);
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
